import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAcceptInvitation } from "@/api/invitations";
import { AgencyLogo } from "@/components/agency-logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { AxiosError } from "axios";

export function AcceptInvitationPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    password: "",
    confirm_password: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const acceptInvitation = useAcceptInvitation();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (form.password !== form.confirm_password) {
      setError("Passwords do not match.");
      return;
    }

    acceptInvitation.mutate(
      {
        token,
        password: form.password,
        first_name: form.first_name,
        last_name: form.last_name,
      },
      {
        onSuccess: () => setSuccess(true),
        onError: (err) => {
          const axiosErr = err as AxiosError<{ error?: { message?: string } }>;
          setError(axiosErr.response?.data?.error?.message || "Failed to accept invitation.");
        },
      }
    );
  };

  if (!token) {
    return (
      <div className="relative flex min-h-screen items-center justify-center bg-background overflow-hidden">
        <div className="login-grid" />
        <div className="login-glow" />
        <div className="relative z-10 w-full max-w-sm rounded-2xl border border-border/60 bg-surface/90 p-8 shadow-elevated backdrop-blur-sm text-center">
          <p className="text-sm text-danger">Invalid invitation link. Missing token.</p>
          <Link to="/login" className="mt-4 inline-block text-sm font-medium text-accent hover:underline">
            Go to Sign In
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-background overflow-hidden">
      <div className="login-grid" />
      <div className="login-glow" />

      <div className="relative z-10 w-full max-w-sm rounded-2xl border border-border/60 bg-surface/90 p-8 shadow-elevated backdrop-blur-sm">
        <div className="mb-10 flex flex-col items-center gap-4">
          <AgencyLogo className="h-8 text-text-primary" />
          <p className="text-[10px] font-medium uppercase tracking-[0.25em] text-text-muted">
            Accept Invitation
          </p>
        </div>

        {success ? (
          <div className="space-y-4 text-center">
            <p className="text-sm text-text-secondary">
              Your account has been set up successfully. You can now sign in.
            </p>
            <Link to="/login" className="text-sm font-medium text-accent hover:underline">
              Sign In
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-text-muted">
                  First Name
                </label>
                <Input
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  required
                  autoFocus
                />
              </div>
              <div>
                <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-text-muted">
                  Last Name
                </label>
                <Input
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  required
                />
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-text-muted">
                Password
              </label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Min 8 characters"
                required
                minLength={8}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-text-muted">
                Confirm Password
              </label>
              <Input
                type="password"
                value={form.confirm_password}
                onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
                required
                minLength={8}
              />
            </div>
            {error && <p className="text-sm text-danger">{error}</p>}
            <Button type="submit" className="w-full" disabled={acceptInvitation.isPending}>
              {acceptInvitation.isPending ? "Setting up..." : "Accept & Create Account"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
